import React, { useState } from 'react';
import { FaArrowLeft, FaArrowRight, FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa';

import axios from 'axios';
import { FaEye, FaSun, FaMoon } from 'react-icons/fa';

const gridImageUrl = 'image/Grid-10-1.png'; // adjust this path

function App() {
  const branchMap = {
    "All Databases": "all",
    "Testing": "employees",
    "Mars Capital": "products",
  };

  const [selectedDb, setSelectedDb] = useState('');
  const [data, setData] = useState({ RecruitmentForms: [] });
  const [searchCnic, setSearchCnic] = useState('');
  const [darkMode, setDarkMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const [loading, setLoading] = useState(false);
  const itemsPerPage = 10;

  const fetchData = () => {
    if (!selectedDb) return;
    setLoading(true);
    setShowTable(false);

    const url = selectedDb === 'all'
      ? 'https://backendats.marsbpo.org/all-db-data'
      : `https://backendats.marsbpo.org/all-db-data/${selectedDb}`;

    axios.get(url)
      .then(res => {
        const combined = selectedDb === 'all'
          ? Object.values(res.data).flatMap(db => db.RecruitmentForms || [])
          : res.data.RecruitmentForms || [];

        setData({ RecruitmentForms: combined });
        setCurrentPage(1);
        setTimeout(() => setShowTable(true), 50);
      })
      .catch(err => alert(err.response?.data?.error || 'Error fetching data'))
      .finally(() => setLoading(false));
  };

  const filterTable = (rows, column) =>
    searchCnic.trim()
      ? rows.filter(row => row[column]?.toString().includes(searchCnic.trim()))
      : rows;

  const paginate = (rows) => {
    const start = (currentPage - 1) * itemsPerPage;
    return rows.slice(start, start + itemsPerPage);
  };

  const columnMap = {
    RecruitmentForms: ['name', 'cnicNo', 'branchName', 'status']
  };

  const columnLabels = {
    name: 'Employee Name',
    cnicNo: 'CNIC',
    branchName: 'Branch Name',
    status: 'Status'
  };

  const renderTable = (rows, title, searchKey, showViewIcon = false) => {
    const filtered = filterTable(rows, searchKey);
    const paginated = paginate(filtered);
    const displayCols = columnMap[title] || [];

    if (loading) {
      return (
        <div className="mt-10 flex flex-col items-center text-blue-600">
          <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-600 border-solid"></div>
          <p className="mt-4 text-lg font-semibold">Loading...</p>
        </div>
      );
    }

    if (!filtered.length) {
      return (
        <div className="mt-10 text-center text-gray-500 dark:text-gray-400 text-lg">
          No results found.
        </div>
      );
    }

    return (
      <div className={`mt-10 shadow-xl rounded-lg overflow-hidden transition-opacity duration-700 ${showTable ? 'opacity-100' : 'opacity-0'}`}>
        <div className="p-4 bg-white dark:bg-gray-800 border-b dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white">{title}</h3>
          <span className="text-sm text-gray-500 dark:text-gray-300">
            Showing {paginated.length} of {filtered.length}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 uppercase text-xs">
              <tr>
                {displayCols.map(col => (
                  <th key={col} className="px-6 py-3 font-medium">{columnLabels[col] || col}</th>
                ))}
                {showViewIcon && <th className="px-6 py-3 text-center">Action</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {paginated.map((row, idx) => (
                <tr key={idx} className="hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                  {displayCols.map((col, i) => (
                    <td key={i} className="px-6 py-4 text-gray-800 dark:text-gray-100">
                      {col.toLowerCase().includes('status') ? (
                        <span className={`text-xs font-bold px-3 py-1 rounded-full inline-block 
                          ${row[col] === 'Hired' ? 'bg-green-100 text-green-700' :
                            row[col] === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                              row[col] === 'Left' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'}`}>
                          {row[col] || 'N/A'}
                        </span>
                      ) : (
                        row[col] || '-'
                      )}
                    </td>
                  ))}
                  {showViewIcon && (
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => setSelectedEmployee(row)}
                        className="text-blue-600 dark:text-blue-400 hover:scale-110 transition transform"
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const maxPages = Math.ceil(filterTable(data.RecruitmentForms, 'cnicNo').length / itemsPerPage);

  return (
    <div className={darkMode ? '' : 'dark'}>

      <div className="banner relative bg-gradient-to-br from-[#0d1117] to-[#161b22] bg-cover bg-center text-[#e1e4e8] text-center p-16 font-sans overflow-hidden min-h-[280px]">
        <div className="banner-content relative z-10 max-w-4xl mx-auto">
          <p className="banner-subtitle uppercase text-[14px] tracking-widest mb-4 text-[#8b94a1]">
            CNIC-BASED EMPLOYEE CHECK
          </p>
          <h1 className="banner-title text-[4rem] font-semibold leading-tight text-white">
            ATs Checker Reliable {' '}<br />
            <em className="italic underline decoration-[#8b94a1] underline-offset-2">
              Employee Verification
            </em>
          </h1>
        </div>
      </div>



      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-10 px-4">
        <div className="max-w-5xl mx-auto bg-white dark:bg-gray-800 shadow-xl rounded-xl p-8 space-y-6">
          <div className="flex justify-between items-center">
            {/* <h1 className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              MARSBPO ALL EMPLOYEES DATA
            </h1> */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="fixed bottom-4 right-2 flex items-center gap-2 px-4 py-2 rounded transition duration-300
    bg-gray-800 text-white dark:bg-white dark:text-black
    shadow-lg hover:shadow-xl
    sm:bottom-6 sm:right-6
    md:bottom-8 md:right-8
    z-50"
              aria-label="Toggle dark/light mode"
            >
              {darkMode ? <FaSun /> : <FaMoon />}
            </button>

          </div>

          <div className="flex flex-col md:flex-row items-center gap-4">
            <select
              value={selectedDb}
              onChange={e => setSelectedDb(e.target.value)}
              className="w-full md:w-1/4 border dark:border-gray-600 rounded px-4 py-2 bg-white dark:bg-gray-700 text-black dark:text-white"
            >
              <option value="">Select Branch</option>
              {Object.entries(branchMap).map(([label, value]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>

            <input
              type="text"
              placeholder="Search by CNIC"
              value={searchCnic}
              onChange={e => setSearchCnic(e.target.value)}
              className="w-full md:flex-1 border dark:border-gray-600 rounded px-4 py-2 bg-white dark:bg-gray-700 text-black dark:text-white"
            />

            <button
              onClick={fetchData}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition"
            >
              Load Data
            </button>
          </div>

          {renderTable(data.RecruitmentForms, 'RecruitmentForms', 'cnicNo', true)}


          {maxPages > 1 && (
            <div className="flex justify-center items-center mt-8 flex-wrap gap-2">
              {/* Prev Button */}
              <button
                onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className={`w-9 h-9 flex items-center justify-center rounded-full text-white 
        ${currentPage === 1 ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                <FaArrowLeft size={14} />
              </button>

              {/* Page Buttons with Ellipsis */}
              {(() => {
                const pageButtons = [];
                const delta = 1;
                const left = Math.max(2, currentPage - delta);
                const right = Math.min(maxPages - 1, currentPage + delta);

                // Show first always
                pageButtons.push(
                  <button
                    key={1}
                    onClick={() => setCurrentPage(1)}
                    className={`w-9 h-9 rounded-full text-sm font-semibold 
            ${currentPage === 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600'}`}
                  >
                    1
                  </button>
                );

                if (left > 2) {
                  pageButtons.push(<span key="left-ellipsis" className="w-9 h-9 flex items-center justify-center">…</span>);
                }

                for (let i = left; i <= right; i++) {
                  pageButtons.push(
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      className={`w-9 h-9 rounded-full text-sm font-semibold 
              ${currentPage === i ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600'}`}
                    >
                      {i}
                    </button>
                  );
                }

                if (right < maxPages - 1) {
                  pageButtons.push(<span key="right-ellipsis" className="w-9 h-9 flex items-center justify-center">…</span>);
                }

                if (maxPages > 1) {
                  pageButtons.push(
                    <button
                      key={maxPages}
                      onClick={() => setCurrentPage(maxPages)}
                      className={`w-9 h-9 rounded-full text-sm font-semibold 
              ${currentPage === maxPages ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600'}`}
                    >
                      {maxPages}
                    </button>
                  );
                }

                return pageButtons;
              })()}

              {/* Next Button */}
              <button
                onClick={() => setCurrentPage(p => Math.min(p + 1, maxPages))}
                disabled={currentPage === maxPages}
                className={`w-9 h-9 flex items-center justify-center rounded-full text-white 
        ${currentPage === maxPages ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                <FaArrowRight size={14} />
              </button>
            </div>
          )}



        </div>
      </div>

      {selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white dark:bg-gray-800 text-black dark:text-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-2xl font-semibold mb-4">Employee Details</h2>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-2">
              {Object.entries(selectedEmployee).map(([key, value]) => (
                <div key={key} className="flex justify-between py-1 border-b text-sm">
                  <span className="font-medium capitalize">{key}:</span>
                  <span className="truncate max-w-[60%]">{value?.toString() || '-'}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setSelectedEmployee(null)}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
